type MdastData = {
  hProperties?: {
    className?: string | Array<string | number>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type MdastNode = {
  type: string;
  value?: string;
  data?: MdastData;
  children?: MdastNode[];
};

const BLOCK_TYPES = new Set([
  'paragraph',
  'heading',
  'listItem',
  'blockquote',
  'tableCell',
]);

const PRE_WRAP_CLASS = 'whitespace-pre-wrap';

const hasSoftBreakInText = (node: MdastNode): boolean => {
  if (node.type === 'text') {
    return typeof node.value === 'string' && node.value.includes('\n');
  }
  if (node.type === 'break') return false;
  if (!node.children) return false;
  return node.children.some(hasSoftBreakInText);
};

const appendClassName = (node: MdastNode): void => {
  const data: MdastData = node.data ?? {};
  const hProperties = data.hProperties ?? {};
  const existing = hProperties.className;
  if (Array.isArray(existing)) {
    if (!existing.includes(PRE_WRAP_CLASS)) {
      hProperties.className = [...existing, PRE_WRAP_CLASS];
    }
  } else if (typeof existing === 'string') {
    hProperties.className = existing.includes(PRE_WRAP_CLASS)
      ? existing
      : `${existing} ${PRE_WRAP_CLASS}`;
  } else {
    hProperties.className = [PRE_WRAP_CLASS];
  }
  data.hProperties = hProperties;
  node.data = data;
};

const walk = (node: MdastNode): void => {
  if (BLOCK_TYPES.has(node.type) && hasSoftBreakInText(node)) {
    appendClassName(node);
  }
  if (node.children) {
    for (const child of node.children) walk(child);
  }
};

/**
 * Legacy markdown content (from the previous WYSIWYG editor) represents
 * in-paragraph line breaks as raw `\n` soft breaks rather than the standard
 * markdown hard break (trailing `\\` or two spaces + `\n`). Standard HTML
 * collapses those newlines, so without intervention the legacy content
 * renders on a single line.
 *
 * This plugin walks the mdast tree and, for any block whose text descendants
 * contain a soft break, asks `mdast-util-to-hast` to set the
 * `whitespace-pre-wrap` class on the resulting element via `data.hProperties`.
 * Content authored with the new editor (which emits proper `<br>` hard breaks)
 * is left alone and renders with default block flow.
 */
const remarkPreserveLegacyNewlines = () => (tree: MdastNode) => {
  walk(tree);
};

export default remarkPreserveLegacyNewlines;
